import { z } from 'zod';

/**
 * Wire shapes for the multi-tenant auth surface. The Worker
 * validates each body against the matching schema; the web client
 * uses the inferred types to keep its forms in sync.
 *
 * Email is lowercased and trimmed at the schema boundary so we
 * never mismatch on casing.
 */

const email = z
  .string()
  .trim()
  .toLowerCase()
  .min(3)
  .max(254)
  .regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Email looks malformed');

const password = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .max(200);

const slug = z
  .string()
  .trim()
  .toLowerCase()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9-]+$/, 'Slug must be kebab-case');

export const registerInput = z.object({
  email,
  password,
  fullName: z.string().min(1).max(120),
  accountName: z.string().min(1).max(120),
  // Optional: registrants who don't pick a property name get one
  // defaulted to "My House" by the route handler.
  propertyName: z.string().min(1).max(120).optional(),
});
export type RegisterInput = z.infer<typeof registerInput>;

export const loginInput = z.object({
  email,
  password: z.string().min(1),
});
export type LoginInput = z.infer<typeof loginInput>;

export const switchAccountInput = z.object({
  accountId: z.string().min(1),
});
export type SwitchAccountInput = z.infer<typeof switchAccountInput>;

export const forgotPasswordInput = z.object({
  email,
});
export type ForgotPasswordInput = z.infer<typeof forgotPasswordInput>;

export const resetPasswordInput = z.object({
  token: z.string().min(8).max(256),
  password,
});
export type ResetPasswordInput = z.infer<typeof resetPasswordInput>;

export const acceptInviteInput = z.object({
  token: z.string().min(8).max(256),
  password: password.optional(),
  fullName: z.string().min(1).max(120).optional(),
});
export type AcceptInviteInput = z.infer<typeof acceptInviteInput>;

export const inviteUserInput = z.object({
  email,
  role: z.enum(['read', 'runner', 'owner', 'admin']),
  propertyIds: z.array(z.string()).optional(),
});
export type InviteUserInput = z.infer<typeof inviteUserInput>;

export const createPropertyInput = z.object({
  name: z.string().min(1).max(120),
  slug: slug.optional(),
  description: z.string().max(2000).nullable().optional(),
  address: z.string().max(500).nullable().optional(),
});
export type CreatePropertyInput = z.infer<typeof createPropertyInput>;

export const patchPropertyInput = z.object({
  name: z.string().min(1).max(120).optional(),
  description: z.string().max(2000).nullable().optional(),
  address: z.string().max(500).nullable().optional(),
  status: z.enum(['active', 'archived']).optional(),
});
export type PatchPropertyInput = z.infer<typeof patchPropertyInput>;

export const patchUserInput = z.object({
  role: z.enum(['read', 'runner', 'owner', 'admin']).optional(),
  propertyIds: z.array(z.string()).nullable().optional(),
});
export type PatchUserInput = z.infer<typeof patchUserInput>;

export type Role = 'read' | 'runner' | 'owner' | 'admin';
